import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const precalculusUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Functions Involving Parameters, Vectors, and Matrices",
  examWeight: "Not on AP Exam",
  focus: "Parametric Functions, Vectors, Matrices, and Linear Transformations",
  sections: [
    {
      id: "unit-4-intro",
      title: "Unit 4 Overview",
      content: (
        <div className="space-y-6">
          <section>
            <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500 mb-4">
              <p className="text-slate-700 font-medium">
                <strong className="text-amber-800">Note:</strong> While Unit 4 topics are not assessed on the AP Precalculus Exam, they are part of the official course framework intended to bridge the gap to higher-level mathematics (Calculus BC, Linear Algebra, and Multivariable Calculus).
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-1",
      title: "Topic 4.1: Parametric Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A parametric function in <InlineMath math="\mathbb{R}^2" /> defines a curve where the <InlineMath math="x" /> and <InlineMath math="y" /> coordinates are independent of each other but dependent on a third variable, <InlineMath math="t" />, called the <strong>parameter</strong>.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="f(t) = (x(t), y(t))" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><InlineMath math="x(t)" />: Function defining the horizontal coordinate</li>
                <li><InlineMath math="y(t)" />: Function defining the vertical coordinate</li>
                <li><strong>Domain:</strong> The set of valid input values for <InlineMath math="t" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Graphing and Orientation</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li><strong>Table of Values:</strong> Generate a table with columns for <InlineMath math="t" />, <InlineMath math="x(t)" />, and <InlineMath math="y(t)" /></li>
              <li><strong>Orientation (Direction):</strong> Unlike standard graphs, parametric curves have direction. Draw arrows to indicate the direction of motion as <InlineMath math="t" /> increases.</li>
              <li><strong>Endpoints:</strong> Because the domain of <InlineMath math="t" /> is often restricted, parametric curves often have distinct <strong>initial points</strong> and <strong>terminal points</strong>.</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Eliminating the Parameter</h3>
            <p className="text-slate-700 mb-4">To convert to rectangular form (<InlineMath math="y = f(x)" />):</p>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Solve one parametric equation for <InlineMath math="t" /></li>
              <li>Substitute that expression into the other equation</li>
            </ol>
            <ImportantNote>
              You must respect the domain restrictions derived from the original parametric equations!
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Parametric Functions Modeling Planar Motion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Context</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Parametric equations are the primary method for modeling particle motion in a 2D plane (e.g., a ball thrown in the air, a bug crawling on a table).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Characteristics</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-slate-700"><strong>Position:</strong> The point <InlineMath math="(x(t), y(t))" /> represents the specific location at time <InlineMath math="t" /></p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-slate-700"><strong>Horizontal Extrema:</strong> Farthest Right = Max of <InlineMath math="x(t)" />; Farthest Left = Min of <InlineMath math="x(t)" /></p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-slate-700"><strong>Vertical Extrema:</strong> Highest Point = Max of <InlineMath math="y(t)" />; Lowest Point = Min of <InlineMath math="y(t)" /></p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-slate-700"><strong>Intercepts:</strong> x-intercepts when <InlineMath math="y(t) = 0" />; y-intercepts when <InlineMath math="x(t) = 0" /></p>
              </div>
            </div>
          </section>

          <ImportantNote>
            Two objects can travel the exact same <strong>path</strong> but with different <strong>motions</strong> (speeds, starting points, or directions) based on how their equations are parametrized.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Parametric Functions and Rates of Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Direction of Motion</h3>
            <p className="text-slate-700 mb-4">Analyze direction by looking at <InlineMath math="x" /> and <InlineMath math="y" /> independently as <InlineMath math="t" /> increases:</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700">If <InlineMath math="x(t)" /> is <strong>increasing</strong>, particle moves <strong>right</strong></p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700">If <InlineMath math="x(t)" /> is <strong>decreasing</strong>, particle moves <strong>left</strong></p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700">If <InlineMath math="y(t)" /> is <strong>increasing</strong>, particle moves <strong>up</strong></p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-slate-700">If <InlineMath math="y(t)" /> is <strong>decreasing</strong>, particle moves <strong>down</strong></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Average Rate of Change (Slope of the Curve)</h3>
            <p className="text-slate-700 mb-4">
              To estimate the slope of the path over an interval <InlineMath math="[t_1, t_2]" />:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="\text{Slope} \approx \frac{\text{Change in } y}{\text{Change in } x} = \frac{y(t_2) - y(t_1)}{x(t_2) - x(t_1)}" />
              <p className="text-sm text-slate-600 mt-2">This represents the slope of the secant line connecting positions at <InlineMath math="t_1" /> and <InlineMath math="t_2" />.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Parametrically Defined Circles and Lines",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parametric Circles</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="font-semibold text-blue-800 mb-2">Standard Parametrization (Counterclockwise):</p>
              <BlockMath math="x(t) = h + r\cos(bt)" />
              <BlockMath math="y(t) = k + r\sin(bt)" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Center:</strong> <InlineMath math="(h, k)" /></li>
                <li><strong>Radius:</strong> <InlineMath math="r" /></li>
                <li><strong>Period:</strong> <InlineMath math="\frac{2\pi}{b}" /></li>
              </ul>
              <p className="text-sm text-slate-600 mt-2">Swapping cosine and sine or changing signs affects the starting point and direction.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parametric Lines</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="font-semibold text-green-800 mb-2">Line Segment from <InlineMath math="(x_1, y_1)" /> to <InlineMath math="(x_2, y_2)" /> over <InlineMath math="0 \le t \le 1" />:</p>
              <BlockMath math="x(t) = x_1 + (x_2 - x_1)t" />
              <BlockMath math="y(t) = y_1 + (y_2 - y_1)t" />
              <p className="text-sm text-slate-600 mt-2">
                <InlineMath math="(x_2 - x_1)" /> is the horizontal velocity; <InlineMath math="(y_2 - y_1)" /> is the vertical velocity.
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Implicitly Defined Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Implicitly Defined Function" 
              definition="A relation where the dependent variable y is not isolated on one side of the equation (e.g., x² + y² = 25). These often fail the vertical line test."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Variable Variation</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-sm text-slate-700">If <InlineMath math="x" /> and <InlineMath math="y" /> change with the <strong>same sign</strong>, the graph is <strong>increasing</strong> (positive slope)</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-sm text-slate-700">If <InlineMath math="x" /> and <InlineMath math="y" /> change with <strong>opposite signs</strong>, the graph is <strong>decreasing</strong> (negative slope)</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-sm text-slate-700"><strong>Vertical Tangents:</strong> Occur when a small change in <InlineMath math="y" /> results in zero change in <InlineMath math="x" /></p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-slate-700"><strong>Horizontal Tangents:</strong> Occur when a small change in <InlineMath math="x" /> results in zero change in <InlineMath math="y" /></p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Conic Sections",
      content: (
        <div className="space-y-6">
          <section>
            <p className="text-slate-700 leading-relaxed mb-4">
              In AP Precalculus, we focus on conics with horizontal or vertical axes of symmetry.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">1. Parabola</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-sm text-slate-700 mb-2">Set of points equidistant from a focus and a directrix.</p>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                <li><strong>Vertical:</strong> <InlineMath math="(x-h)^2 = 4p(y-k)" /></li>
                <li><strong>Horizontal:</strong> <InlineMath math="(y-k)^2 = 4p(x-h)" /></li>
                <li>Vertex: <InlineMath math="(h, k)" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">2. Ellipse</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <p className="text-sm text-slate-700 mb-2">Set of points where the sum of distances to two foci is constant.</p>
              <BlockMath math="\frac{(x-h)^2}{a^2} + \frac{(y-k)^2}{b^2} = 1" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li>Center: <InlineMath math="(h, k)" /></li>
                <li>If <InlineMath math="a > b" />, elongated horizontally</li>
                <li>If <InlineMath math="b > a" />, elongated vertically</li>
                <li><strong>Circle:</strong> Special case where <InlineMath math="a = b = r" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">3. Hyperbola</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <p className="text-sm text-slate-700 mb-2">Set of points where the difference of distances to two foci is constant.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Opens Left/Right:</strong></p>
              <BlockMath math="\frac{(x-h)^2}{a^2} - \frac{(y-k)^2}{b^2} = 1" />
              <p className="text-sm text-slate-700 mt-2"><strong>Opens Up/Down:</strong></p>
              <BlockMath math="\frac{(y-k)^2}{a^2} - \frac{(x-h)^2}{b^2} = 1" />
              <ImportantNote>
                The positive term dictates the direction of opening.
              </ImportantNote>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-7",
      title: "Topic 4.7: Parametrization of Implicitly Defined Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parametrizing Functions</h3>
            <p className="text-slate-700 mb-4">
              If <InlineMath math="y = f(x)" />, a trivial parametrization is:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="x(t) = t" />
              <BlockMath math="y(t) = f(t)" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Parametrizing Conics</h3>
            <p className="text-slate-700 mb-4">
              Using Pythagorean Identities (<InlineMath math="\cos^2 t + \sin^2 t = 1" /> and <InlineMath math="\sec^2 t - \tan^2 t = 1" />):
            </p>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Ellipses</h4>
                <p className="text-sm text-slate-700 mt-1">For <InlineMath math="\frac{(x-h)^2}{a^2} + \frac{(y-k)^2}{b^2} = 1" />:</p>
                <BlockMath math="x(t) = h + a\cos(t)" />
                <BlockMath math="y(t) = k + b\sin(t)" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Hyperbolas (Left/Right)</h4>
                <p className="text-sm text-slate-700 mt-1">For <InlineMath math="\frac{(x-h)^2}{a^2} - \frac{(y-k)^2}{b^2} = 1" />:</p>
                <BlockMath math="x(t) = h + a\sec(t)" />
                <BlockMath math="y(t) = k + b\tan(t)" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-8",
      title: "Topic 4.8: Vectors",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Vector" 
              definition="A quantity with both magnitude (length) and direction. Represented geometrically as a directed line segment (arrow)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Component Form</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-slate-700">For a vector <InlineMath math="v" /> starting at <InlineMath math="P(x_1, y_1)" /> and ending at <InlineMath math="Q(x_2, y_2)" />:</p>
              <BlockMath math="v = \langle x_2-x_1, y_2-y_1 \rangle = \langle a, b \rangle" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Vector Operations</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-slate-700"><strong>Magnitude (Norm):</strong> <InlineMath math="\|v\| = \sqrt{a^2 + b^2}" /></p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-slate-700"><strong>Scalar Multiplication:</strong> <InlineMath math="k \cdot \langle a, b \rangle = \langle ka, kb \rangle" /></p>
                <p className="text-sm text-slate-600">(Scales length; reverses direction if <InlineMath math="k < 0" />)</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-slate-700"><strong>Vector Addition:</strong> <InlineMath math="\langle a, b \rangle + \langle c, d \rangle = \langle a+c, b+d \rangle" /></p>
                <p className="text-sm text-slate-600">(Geometrically: "Tail-to-Head" method)</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-slate-700"><strong>Unit Vector:</strong> A vector of length 1 in the same direction: <InlineMath math="\frac{v}{\|v\|}" /></p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <p className="text-slate-700"><strong>Standard Unit Vectors:</strong> <InlineMath math="i = \langle 1, 0 \rangle" /> and <InlineMath math="j = \langle 0, 1 \rangle" /></p>
                <p className="text-sm text-slate-600"><InlineMath math="v = ai + bj" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Dot Product</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="u \cdot v = a_1a_2 + b_1b_2" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li>The result is a <strong>scalar</strong> (number), not a vector</li>
                <li><strong>Angle between vectors:</strong> <InlineMath math="\cos \theta = \frac{u \cdot v}{\|u\|\|v\|}" /></li>
                <li><strong>Orthogonal:</strong> If <InlineMath math="u \cdot v = 0" />, the vectors are perpendicular (90°)</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-9",
      title: "Topic 4.9: Vector-Valued Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <KeyTerm 
              term="Vector-Valued Function" 
              definition="A function where the input is a scalar (t) and the output is a vector."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mt-4">
              <BlockMath math="v(t) = \langle x(t), y(t) \rangle" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Context (Motion)</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Position Vector</h4>
                <p className="text-sm text-slate-700 mt-1">The vector from the origin <InlineMath math="(0,0)" /> to the particle's position <InlineMath math="(x(t), y(t))" />. Magnitude represents distance from the origin.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Velocity Vector</h4>
                <p className="text-sm text-slate-700 mt-1">Components represent instantaneous velocity in <InlineMath math="x" /> and <InlineMath math="y" /> directions. Magnitude represents <strong>speed</strong>.</p>
                <p className="text-sm text-slate-600 mt-1">Signs indicate direction (negative x-component = moving left)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-10",
      title: "Topic 4.10: Matrices",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition</h3>
            <KeyTerm 
              term="Matrix" 
              definition="A rectangular array of numbers with n rows and m columns (n × m matrix)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Matrix Multiplication</h3>
            <p className="text-slate-700 mb-4">To find the product <InlineMath math="AB" />:</p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700"><strong>Condition:</strong> The number of <strong>columns</strong> in <InlineMath math="A" /> must equal the number of <strong>rows</strong> in <InlineMath math="B" />.</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li><strong>Result Size:</strong> If <InlineMath math="A" /> is <InlineMath math="n \times k" /> and <InlineMath math="B" /> is <InlineMath math="k \times m" />, the product is <InlineMath math="n \times m" /></li>
                <li><strong>Process:</strong> Element in row <InlineMath math="i" />, column <InlineMath math="j" /> of the product is the <strong>dot product</strong> of the <InlineMath math="i" />-th row of <InlineMath math="A" /> and the <InlineMath math="j" />-th column of <InlineMath math="B" /></li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-11",
      title: "Topic 4.11: The Inverse and Determinant of a Matrix",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determinant (for 2×2)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700">For matrix <InlineMath math="A = \begin{bmatrix} a & b \\ c & d \end{bmatrix}" />:</p>
              <BlockMath math="\det(A) = ad - bc" />
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li><strong>Geometric Meaning:</strong> The absolute value represents the area of the parallelogram formed by the column vectors</li>
                <li><strong>Singularity:</strong> If <InlineMath math="\det(A) = 0" />, the matrix has no inverse (singular)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Inverse Matrix</h3>
            <p className="text-slate-700 mb-4">
              The inverse <InlineMath math="A^{-1}" /> satisfies <InlineMath math="A \cdot A^{-1} = I" /> (Identity Matrix).
            </p>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <p className="text-slate-700">For a 2×2 matrix:</p>
              <BlockMath math="A^{-1} = \frac{1}{ad-bc} \begin{bmatrix} d & -b \\ -c & a \end{bmatrix}" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Identity Matrix</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700">A square matrix with 1s on the main diagonal and 0s elsewhere:</p>
              <BlockMath math="I = \begin{bmatrix} 1 & 0 \\ 0 & 1 \end{bmatrix}" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-12",
      title: "Topic 4.12: Linear Transformations and Matrices",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Matrices can function as operators that transform vectors. A linear transformation <InlineMath math="L" /> maps an input vector <InlineMath math="u" /> to an output vector <InlineMath math="v" /> via matrix multiplication:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <BlockMath math="L(u) = A \cdot u" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Properties</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Maps the zero vector to the zero vector</li>
              <li>Maps lines to lines (or points)</li>
              <li>Preserves grid parallelism</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the Transformation Matrix</h3>
            <Mnemonic 
              title="Standard Basis Method"
              content="To determine the matrix for a transformation, look at where the standard basis vectors i = ⟨1, 0⟩ and j = ⟨0, 1⟩ land. Column 1 is where i lands; Column 2 is where j lands."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-4-13",
      title: "Topic 4.13: Matrices as Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Geometric Transformations (2×2)</h3>
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Rotation (Counterclockwise by θ)</h4>
                <BlockMath math="\begin{bmatrix} \cos \theta & -\sin \theta \\ \sin \theta & \cos \theta \end{bmatrix}" />
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Reflection (over x-axis)</h4>
                <BlockMath math="\begin{bmatrix} 1 & 0 \\ 0 & -1 \end{bmatrix}" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Reflection (over y-axis)</h4>
                <BlockMath math="\begin{bmatrix} -1 & 0 \\ 0 & 1 \end{bmatrix}" />
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Dilation (Scale factor k)</h4>
                <BlockMath math="\begin{bmatrix} k & 0 \\ 0 & k \end{bmatrix}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Composition</h3>
            <ImportantNote>
              Applying transformation <InlineMath math="A" />, then transformation <InlineMath math="B" />, is equivalent to multiplying the matrices in reverse order: <InlineMath math="B \cdot A" />. (Function composition notation: <InlineMath math="B(A(x))" />)
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-14",
      title: "Topic 4.14: Matrices Modeling Contexts",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">State Vectors and Transition Matrices</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Matrices are used to model systems that change over discrete time steps (Markov Chains).
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">State Vector (<InlineMath math="S_t" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Represents the state of a system at time <InlineMath math="t" /> (e.g., population distribution between city and suburbs)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Transition Matrix (<InlineMath math="T" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Contains probabilities/rates of moving from one state to another. Columns must sum to 1 for probability matrices.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Modeling</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <ul className="list-disc pl-5 space-y-2 text-slate-700">
                <li><strong>Future State:</strong> <InlineMath math="S_{t+1} = T \cdot S_t" /></li>
                <li><strong>n-steps later:</strong> <InlineMath math="S_{t+n} = T^n \cdot S_t" /></li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Steady State</h3>
            <KeyTerm 
              term="Steady State" 
              definition="A state that does not change after the transformation: T · S = S. This represents the long-term behavior of the system."
            />
          </section>
        </div>
      )
    },
    {
      id: "summary-4",
      title: "Unit 4 Summary: Key Skills",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Parametric Functions</h4>
                  <p className="text-sm text-slate-700 mt-1">Graph <InlineMath math="(x(t), y(t))" /> with direction arrows. Eliminate parameter to convert to rectangular form.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Conic Sections</h4>
                  <p className="text-sm text-slate-700 mt-1">Identify and parametrize parabolas, ellipses, hyperbolas using standard forms.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Vector Operations</h4>
                  <p className="text-sm text-slate-700 mt-1">Add vectors, multiply by scalars, find magnitude, dot product, and angle between vectors.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Matrix Operations</h4>
                  <p className="text-sm text-slate-700 mt-1">Multiply matrices, find determinant and inverse of 2×2 matrices.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Transformations</h4>
                  <p className="text-sm text-slate-700 mt-1">Apply rotation, reflection, and dilation matrices. Understand composition order.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default precalculusUnit4Notes;
